import { Theme } from '../types/theme';

// Default theme (current orange/primary theme)
export const defaultTheme: Theme = {
  id: 'default',
  name: 'Restaurant Default',
  colors: {
    primary: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316', // Main orange
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
    secondary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    accent: '#10b981',
    background: '#ffffff',
    surface: '#f9fafb',
    error: '#ef4444',
    warning: '#f59e0b',
    success: '#10b981',
    text: {
      primary: '#111827',
      secondary: '#6b7280',
      disabled: '#d1d5db',
    },
  },
  branding: {
    name: 'Voice Restaurant',
    font: {
      primary: 'Inter, sans-serif',
      secondary: 'Inter, sans-serif',
    },
    borderRadius: {
      small: '0.375rem',
      medium: '0.5rem',
      large: '0.75rem',
    },
    shadows: {
      small: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      medium: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      large: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    },
  },
  cssVariables: {
    '--color-primary': '#f97316',
    '--color-primary-dark': '#ea580c',
    '--color-secondary': '#64748b',
    '--color-accent': '#10b981',
    '--color-background': '#ffffff',
    '--color-surface': '#f9fafb',
    '--color-text-primary': '#111827',
    '--color-text-secondary': '#6b7280',
  },
};

// KFC Theme - Red and white with black accents
export const kfcTheme: Theme = {
  id: 'kfc',
  name: 'KFC Style',
  colors: {
    primary: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#dc2626', // KFC Red
      600: '#b91c1c',
      700: '#991b1b',
      800: '#7f1d1d',
      900: '#7c2d12',
    },
    secondary: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827', // KFC Black
    },
    accent: '#fbbf24', // KFC Yellow/Gold
    background: '#ffffff',
    surface: '#f9fafb',
    error: '#dc2626',
    warning: '#f59e0b',
    success: '#059669',
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      disabled: '#d1d5db',
    },
  },
  branding: {
    name: 'KFC Style Restaurant',
    font: {
      primary: 'Georgia, serif', // KFC uses serif fonts
      secondary: 'Arial, sans-serif',
    },
    borderRadius: {
      small: '0.25rem',
      medium: '0.375rem',
      large: '0.5rem',
    },
    shadows: {
      small: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      medium: '0 4px 6px -1px rgb(0 0 0 / 0.15), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      large: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    },
  },
  cssVariables: {
    '--color-primary': '#dc2626',
    '--color-primary-dark': '#b91c1c',
    '--color-secondary': '#111827',
    '--color-accent': '#fbbf24',
    '--color-background': '#ffffff',
    '--color-surface': '#f9fafb',
    '--color-text-primary': '#111827',
    '--color-text-secondary': '#4b5563',
  },
};

// McDonald's Theme - Red and yellow
export const mcdonaldsTheme: Theme = {
  id: 'mcdonalds',
  name: "McDonald's Style",
  colors: {
    primary: {
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',
      500: '#eab308', // McDonald's Yellow
      600: '#ca8a04',
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
    },
    secondary: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#da1e37', // McDonald's Red
      600: '#b91c1c',
      700: '#991b1b',
      800: '#7f1d1d',
      900: '#7c2d12',
    },
    accent: '#059669', // Green for healthy options
    background: '#ffffff',
    surface: '#fffbeb',
    error: '#da1e37',
    warning: '#f59e0b',
    success: '#059669',
    text: {
      primary: '#292524',
      secondary: '#57534e',
      disabled: '#d6d3d1',
    },
  },
  branding: {
    name: "McDonald's Style Restaurant",
    font: {
      primary: 'Arial, sans-serif', // McDonald's uses clean sans-serif
      secondary: 'Helvetica, sans-serif',
    },
    borderRadius: {
      small: '0.5rem',
      medium: '0.75rem',
      large: '1rem',
    },
    shadows: {
      small: '0 2px 4px 0 rgb(218 30 55 / 0.1)',
      medium: '0 4px 8px 0 rgb(218 30 55 / 0.12), 0 2px 4px 0 rgb(0 0 0 / 0.08)',
      large: '0 20px 40px 0 rgb(218 30 55 / 0.15)',
    },
  },
  cssVariables: {
    '--color-primary': '#eab308',
    '--color-primary-dark': '#ca8a04',
    '--color-secondary': '#da1e37',
    '--color-accent': '#059669',
    '--color-background': '#ffffff',
    '--color-surface': '#fffbeb',
    '--color-text-primary': '#292524',
    '--color-text-secondary': '#57534e',
  },
};

// Subway Theme - Green and yellow
export const subwayTheme: Theme = {
  id: 'subway',
  name: 'Subway Style',
  colors: {
    primary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#00923f', // Subway Green
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    secondary: {
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',
      500: '#ffdc00', // Subway Yellow
      600: '#ca8a04',
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
    },
    accent: '#f97316',
    background: '#ffffff',
    surface: '#f0fdf4',
    error: '#dc2626',
    warning: '#f59e0b',
    success: '#00923f',
    text: {
      primary: '#1f2937',
      secondary: '#4b5563',
      disabled: '#d1d5db',
    },
  },
  branding: {
    name: 'Subway Style Restaurant',
    font: {
      primary: 'Arial, sans-serif',
      secondary: 'Helvetica, sans-serif',
    },
    borderRadius: {
      small: '0.25rem',
      medium: '0.5rem',
      large: '0.75rem',
    },
    shadows: {
      small: '0 1px 2px 0 rgb(0 146 63 / 0.1)',
      medium: '0 4px 6px -1px rgb(0 146 63 / 0.15), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      large: '0 20px 25px -5px rgb(0 146 63 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    },
  },
  cssVariables: {
    '--color-primary': '#00923f',
    '--color-primary-dark': '#15803d',
    '--color-secondary': '#ffdc00',
    '--color-accent': '#f97316',
    '--color-background': '#ffffff',
    '--color-surface': '#f0fdf4',
    '--color-text-primary': '#1f2937',
    '--color-text-secondary': '#4b5563',
  },
};

export const availableThemes: Theme[] = [
  defaultTheme,
  kfcTheme,
  mcdonaldsTheme,
  subwayTheme,
];

export const getThemeById = (id: string): Theme => {
  return availableThemes.find(theme => theme.id === id) || defaultTheme;
};